import React, { useEffect, useMemo, useRef, useState } from "react";
import './StudioSection.css';

/**
 * Normaliza un string para búsqueda: minúsculas, sin tildes, sin guiones
 */
const normalize = (str) =>
  str.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/-/g, ' ');

/**
 * Retorna el label de display de un item (objeto o string legacy)
 */
const getItemLabel = (item) =>
  typeof item === 'string'
    ? item
    : [item.brand, item.model].filter(Boolean).join(' ');

/**
 * Retorna el texto buscable de un item (objeto o string legacy)
 */
const getItemSearchText = (item) => {
  if (typeof item === 'string') return normalize(item);
  const parts = [
    item.brand || '',
    item.model || '',
    ...(item.tags || []),
    item.notes || '',
  ];
  return normalize(parts.join(' '));
};

/**
 * StudioSection - Sección para mostrar el estudio físico
 * Incluye galería de salas y desplegables con equipamiento
 */
const StudioSection = ({ title, subtitle, rooms = [], equipment = [] }) => {
  const [selectedRoom, setSelectedRoom] = useState(0);
  const [activeEquipment, setActiveEquipment] = useState(0);
  const [equipmentQuery, setEquipmentQuery] = useState('');
  const lightboxRef = useRef(null);

  useEffect(() => {
    if (selectedRoom >= rooms.length) setSelectedRoom(0);
  }, [rooms.length, selectedRoom]);

  useEffect(() => {
    if (activeEquipment >= equipment.length) {
      setActiveEquipment(0);
      setEquipmentQuery('');
    }
  }, [equipment.length, activeEquipment]);

  const room = rooms[selectedRoom] || null;
  const equipmentCategory = equipment[activeEquipment] || null;

  const filteredItems = useMemo(() => {
    if (!equipmentCategory?.items) return [];
    const query = normalize(equipmentQuery.trim());
    if (!query) return equipmentCategory.items;
    return equipmentCategory.items.filter((item) =>
      getItemSearchText(item).includes(query)
    );
  }, [equipmentCategory, equipmentQuery]);

  const openLightbox = () => lightboxRef.current?.showModal();
  const closeLightbox = () => lightboxRef.current?.close();

  return (
    <div className="studioContainer">
      {title && <h2 className="studioTitle">{title.toUpperCase()}</h2>}
      {subtitle && <p className="studioSubtitle">{subtitle}</p>}

      <div className="studioLayout">
        {/* Galería de salas */}
        {rooms.length > 0 && room && (
          <div className="studioGalleryPanel">
            <div className="roomHeroCard">
              <div
                className="roomImageWrapper"
                onClick={openLightbox}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openLightbox()}
              >
                <img src={room.image} alt={room.name} className="roomImage" />
                <div className="roomImageOverlay">
                  <h3 className="roomName">{room.name}</h3>
                  {room.description && (
                    <p className="roomDescription">{room.description}</p>
                  )}
                </div>
                <div className="roomImageZoomHint">⛶</div>
              </div>

              <div className="roomsThumbs" aria-label="Salas del estudio">
                {rooms.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`roomThumbButton ${selectedRoom === index ? 'active' : ''}`}
                    onClick={() => setSelectedRoom(index)}
                  >
                    <img src={item.image} alt={item.name} className="roomThumbImage" />
                    <span className="roomThumbLabel">{item.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Equipamiento con tabs */}
        {equipment.length > 0 && (
          <div className="equipmentPanel">
            <h3 className="equipmentSectionTitle">NUESTRO EQUIPAMIENTO</h3>

            <div className="equipmentTabs">
              {equipment.map((category, index) => (
                <button
                  key={index}
                  className={`equipmentTab ${activeEquipment === index ? 'active' : ''}`}
                  onClick={() => { setActiveEquipment(index); setEquipmentQuery(''); }}
                  type="button"
                >
                  <span className="equipmentTabIcon">{category.icon}</span>
                  <span className="equipmentTabTitle">{category.category}</span>
                  <span className="equipmentTabCount">({category.items.length})</span>
                </button>
              ))}
            </div>

            {equipmentCategory && (
              <div className="equipmentContent">
                <input
                  type="text"
                  value={equipmentQuery}
                  onChange={(e) => setEquipmentQuery(e.target.value)}
                  placeholder="Buscar por nombre, tipo, marca..."
                  className="equipmentSearch"
                />

                <div className="equipmentListChips">
                  {filteredItems.map((item, index) => (
                    <span className="equipmentChip" key={index}>
                      {getItemLabel(item)}
                    </span>
                  ))}
                  {filteredItems.length === 0 && (
                    <p className="equipmentEmpty">No encontramos equipos con ese criterio.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {room && (
        <dialog ref={lightboxRef} className="roomLightbox" onClick={closeLightbox}>
          <img src={room.image} alt={room.name} className="roomLightboxImage" />
        </dialog>
      )}
    </div>
  );
};

export default StudioSection;
